// src/data/resources/articles/troubleshooting.ts

export const troubleshootingArticles = [
    {
      id: "common-issues",
      categoryId: "troubleshooting",
      title: "Common Issues & Solutions",
      content: `
        <h2>Common Issues & Solutions</h2>
        <p>This guide addresses the most frequent issues MailMaid users encounter and provides step-by-step solutions.</p>
        
        <h3>Connection Problems</h3>
        <p><strong>Issue: Unable to connect email account</strong></p>
        <p>Solutions:</p>
        <ol>
          <li><strong>Check credentials:</strong> Verify you're using the correct email and password</li>
          <li><strong>Two-factor authentication:</strong> If enabled, use an app-specific password</li>
          <li><strong>Provider settings:</strong>
            <ul>
              <li>Gmail: Ensure IMAP is enabled and "less secure apps" is allowed if needed</li>
              <li>Outlook: Check if your admin has restricted third-party apps</li>
              <li>Other providers: Verify IMAP/POP3 access is enabled</li>
            </ul>
          </li>
          <li><strong>Firewall or security software:</strong> Temporarily disable to check if it's blocking the connection</li>
          <li><strong>Network issues:</strong> Try connecting on a different network</li>
        </ol>
        
        <p><strong>Issue: Email account disconnects frequently</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Check your internet connection stability</li>
          <li>Verify your email provider is not experiencing outages</li>
          <li>Review password expiration policies for your email account</li>
          <li>Ensure you haven't exceeded login attempt limits</li>
          <li>Try removing and re-adding the account</li>
        </ol>
        
        <h3>Synchronization Issues</h3>
        <p><strong>Issue: Emails not appearing in MailMaid</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Check the sync status indicator on the dashboard</li>
          <li>Manually trigger a sync from Settings > Email Accounts</li>
          <li>Verify sync settings to ensure the correct folders are being synchronized</li>
          <li>Check if filters or rules are unintentionally hiding emails</li>
          <li>For large inboxes, allow more time for the initial sync to complete</li>
        </ol>
        
        <p><strong>Issue: Changes in MailMaid not reflecting in email client</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Verify two-way sync is enabled in Settings > Sync Options</li>
          <li>Allow up to 5 minutes for changes to propagate</li>
          <li>Check if your email provider has rules that might be interfering</li>
          <li>Try manually refreshing your email client</li>
          <li>Restart the MailMaid app to trigger a fresh sync</li>
        </ol>
        
        <h3>Feature-Specific Issues</h3>
        <p><strong>Issue: Smart Categorization placing emails incorrectly</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Drag misplaced emails to the correct category to train the AI</li>
          <li>Review and adjust category rules in Settings > Categories</li>
          <li>Use the "This is miscategorized" feedback option on problematic emails</li>
          <li>For persistent issues, reset category learning from Advanced Settings</li>
          <li>Create custom rules for specific senders that are consistently miscategorized</li>
        </ol>
        
        <p><strong>Issue: AI Response Suggestions not appearing</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Verify the feature is enabled in Settings > AI Features</li>
          <li>Check if the email language is supported</li>
          <li>Ensure the email has sufficient content for generating suggestions</li>
          <li>Try with a different email to see if it's specific to certain messages</li>
          <li>Check your subscription tier—some AI features require Pro or Enterprise</li>
        </ol>
        
        <h3>Performance Issues</h3>
        <p><strong>Issue: MailMaid running slowly</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Check your internet connection speed</li>
          <li>Clear the application cache from Settings > Advanced > Clear Cache</li>
          <li>Reduce the number of emails displayed per page in Settings > Display</li>
          <li>If using the web version, try a different browser</li>
          <li>For desktop app, ensure your computer meets the minimum requirements</li>
          <li>Consider archiving older emails to reduce database size</li>
        </ol>
        
        <p><strong>Issue: High memory or CPU usage</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Close and restart the application</li>
          <li>Disable unnecessary browser extensions if using the web version</li>
          <li>Reduce the number of connected email accounts if possible</li>
          <li>Update to the latest version of MailMaid</li>
          <li>Adjust sync frequency to less frequent intervals</li>
        </ol>
        
        <h3>Account & Billing Issues</h3>
        <p><strong>Issue: Unable to upgrade or change subscription</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Verify your payment method is valid and has sufficient funds</li>
          <li>Try an alternative payment method</li>
          <li>Clear your browser cache and cookies if using the web version</li>
          <li>Check if there are any regional restrictions for your payment method</li>
          <li>Contact billing support at billing@mailmaid.com</li>
        </ol>
        
        <p><strong>Issue: Missing Pro features after upgrade</strong></p>
        <p>Solutions:</p>
        <ol>
          <li>Verify your payment was successfully processed</li>
          <li>Check your email for purchase confirmation</li>
          <li>Log out and log back in to refresh your subscription status</li>
          <li>Clear browser cache if using the web version</li>
          <li>Contact customer support with your transaction ID</li>
        </ol>
        
        <h3>Still Experiencing Issues?</h3>
        <p>If you've tried the solutions above and are still experiencing problems:</p>
        <ol>
          <li>Check our <a href="/resources/troubleshooting">Troubleshooting section</a> for more specific guides</li>
          <li>Visit the <a href="/status">System Status page</a> to see if there are any ongoing service issues</li>
          <li>Contact our support team through the in-app chat or at support@mailmaid.com</li>
          <li>Include the following details in your support request:
            <ul>
              <li>Your MailMaid account email</li>
              <li>Device and browser/app version you're using</li>
              <li>Detailed description of the issue</li>
              <li>Any error messages you received</li>
              <li>Steps you've already taken to resolve the issue</li>
            </ul>
          </li>
        </ol>
        
        <p>Our support team typically responds within 24 hours on business days.</p>
      `,
      excerpt: "Find solutions for the most common issues MailMaid users encounter and learn how to resolve them quickly.",
      order: 1
    }
  ];